<script setup lang="ts">
import { cn } from "@voltalis/frontend-utils";
import { Loading01Icon } from "@voltalis/ui-kit/icons";
import { computed, useSlots, type Component } from "vue";

import { HospitalityButtonSeverity } from "@/components/ui/buttons/hospitality-button.types";

const props = withDefaults(
  defineProps<{
    severity?: HospitalityButtonSeverity;
    disabled?: boolean;
    loading?: boolean;
    label?: string;
    size?: "md" | "sm" | "xs";
    type?: "button" | "submit" | "reset";
    /** Before the label (left) **/
    icon?: Component;
    /** After the label (right) **/
    trailingIcon?: Component;
    /** Accessible name. Required for icon-only buttons, which have no visible label. */
    ariaLabel?: string;
    isIconOnly?: boolean;
  }>(),
  {
    severity: "primary",
    type: "button",
    disabled: false,
    loading: false,
    size: "md",
  },
);

const slots = useSlots();

const isDisabled = computed<boolean>(() => props.disabled || props.loading);
const hasLabel = computed<boolean>(() => !!props.label || !!slots.default);
const isIconOnly = computed<boolean>(
  () => props.isIconOnly || (!hasLabel.value && (!!props.icon || !!props.trailingIcon)),
);
</script>

<template>
  <button
    :class="
      cn('hospitality-button', props.severity, props.size, {
        disabled: props.disabled,
        loading: props.loading,
        'icon-only': isIconOnly,
      })
    "
    :disabled="isDisabled"
    :aria-label="props.ariaLabel"
    :aria-busy="props.loading || undefined"
    :type="props.type"
  >
    <!-- While loading, the spinner takes the leading icon's place and the trailing icon is hidden. -->
    <Loading01Icon
      v-if="props.loading"
      class="hospitality-button-spinner"
      aria-hidden="true"
    />
    <component
      v-else-if="props.icon"
      :is="props.icon"
      class="hospitality-button-icon"
      aria-hidden="true"
    />
    <span
      v-if="hasLabel"
      class="hospitality-button-label"
    >
      <slot>{{ props.label }}</slot>
    </span>
    <component
      :is="props.trailingIcon"
      v-if="props.trailingIcon && !props.loading"
      class="hospitality-button-icon"
      aria-hidden="true"
    />
  </button>
</template>

<style lang="css" scoped>
/*
 * Component tokens: each severity only sets these variables, which point to semantic tokens (--vt-color-*).
 * Their values follow the active [data-brand] and the light/dark `color-scheme`.
 */
.hospitality-button {
  --button-bg: transparent;
  --button-color: var(--vt-color-text-link);
  --button-border: transparent;
  --button-bg-hover: var(--button-bg);
  --button-color-hover: var(--button-color);
  --button-border-hover: var(--button-border);
  /* Disabled: faded by default, severities on light surfaces switch to neutral colors instead. */
  --button-bg-disabled: var(--button-bg);
  --button-color-disabled: var(--button-color);
  --button-border-disabled: var(--button-border);
  --button-opacity-disabled: 0.4;

  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: var(--vt-spacing-sm);
  border: 1px solid var(--button-border);
  border-radius: var(--vt-radius-md);
  background-color: var(--button-bg);
  color: var(--button-color);
  font-family: var(--vt-font-sans);
  white-space: nowrap;
  cursor: pointer;

  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;

  /* Icons follow the label's font size. */
  & .hospitality-button-icon,
  & .hospitality-button-spinner {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
  }

  &:not(:disabled):hover {
    border-color: var(--button-border-hover);
    background-color: var(--button-bg-hover);
    color: var(--button-color-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--vt-color-border-focus);
    outline-offset: 2px;
  }

  &.disabled {
    border-color: var(--button-border-disabled);
    background-color: var(--button-bg-disabled);
    color: var(--button-color-disabled);
    opacity: var(--button-opacity-disabled);
    cursor: not-allowed;
  }

  &.loading {
    cursor: progress;
  }

  /* size */
  &.md {
    height: 48px;
    padding-inline: var(--vt-spacing-xl);
    font-size: var(--vt-typo-body-button-main-font-size, var(--vt-typo-body-label-font-size));
    line-height: var(--vt-typo-body-button-main-line-height, var(--vt-typo-body-label-line-height));
    letter-spacing: var(--vt-typo-body-button-main-letter-spacing, var(--vt-typo-body-label-letter-spacing));
  }

  &.sm {
    height: 42px;
    padding-inline: var(--vt-spacing-lg);
  }

  &.xs {
    height: 34px;
    padding-inline: var(--vt-spacing-md);
  }

  &.sm,
  &.xs {
    font-size: var(--vt-typo-body-button-font-size, var(--vt-typo-body-data-font-size));
    line-height: var(--vt-typo-body-button-line-height, var(--vt-typo-body-data-line-height));
    letter-spacing: var(--vt-typo-body-button-letter-spacing, var(--vt-typo-body-data-letter-spacing));
    font-weight: var(--vt-font-weight-bold);
  }

  /* Icon-only buttons are round: as wide as they are tall. */
  &.icon-only {
    aspect-ratio: 1;
    padding-inline: 0;
    border-radius: var(--vt-radius-full);
  }

  /* severity */
  &.primary {
    --button-bg: var(--vt-color-action-primary);
    --button-bg-hover: var(--vt-color-action-primary-hover);
    --button-color: var(--vt-color-text-on-action);
  }

  &.secondary {
    --button-bg: var(--vt-color-bg-card);
    --button-color: var(--vt-color-text-secondary);
    --button-border: var(--vt-color-border-default);
    --button-border-hover: var(--vt-color-border-strong);
    --button-bg-disabled: var(--vt-color-bg-disabled);
    --button-color-disabled: var(--vt-color-text-disabled);
    --button-border-disabled: transparent;
    --button-opacity-disabled: 1;
  }

  &.tertiary {
    --button-color: var(--vt-color-text-secondary);
    --button-color-hover: var(--vt-color-text-primary);
    --button-color-disabled: var(--vt-color-text-disabled);
    --button-opacity-disabled: 1;
  }

  &.link {
    --button-color: var(--vt-color-text-link);
    --button-color-disabled: var(--vt-color-text-disabled);
    --button-opacity-disabled: 1;

    padding-inline: 0;

    &:not(:disabled):hover .hospitality-button-label {
      text-decoration: underline;
    }

    &:not(.md) {
      height: unset !important;
      justify-content: unset !important;
    }
  }

  &.destructive {
    --button-bg: var(--vt-color-action-destructive);
    --button-bg-hover: var(--vt-color-action-destructive-hover);
    --button-color: var(--vt-color-text-on-action);
  }
}

.hospitality-button-label {
  font-weight: var(--vt-font-weight-semibold);
  display: inline-flex;
  align-items: center;
}

.hospitality-button-spinner {
  animation: hospitality-button-spin 1s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 3s;
  }
}

@keyframes hospitality-button-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
